import React, { useRef, useState, useEffect } from "react";
import { Button, Container, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider} from '@react-oauth/google';
import Alert from "../components/Alert";
import { useLang, useLanguage } from "../contexts/LanguageContext";

import axios from "axios";
const Home = () => {
  const [error, setError] = useState(""); // Error state for feedback
  const [loading, setLoading] = useState(true); // Loading state to prevent flashing content

  const [functionError, setFunctionError] = useState("");
  const [functionSuccess, setFunctionSuccess] = useState("");
  
  const navigate = useNavigate(); // For navigation after login
  const { setLang } = useLang();
  const L = useLanguage();

  

  const emailRef = useRef(); // Email input ref
  const passwordRef = useRef(); // Password input ref
  const resetEmailRef = useRef(); // Reset email input ref
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const newPasswordRef = useRef();
  const confirmPasswordRef = useRef();

  // Check for reset token in URL on component mount
  useEffect( () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setShowResetForm(true);
    }
  }, []);

  const successMessage = async (response) => {
	const tokenId = response.credential;
  
	
  
	try {
	  axios.post(
		'https://api.viralgpt.com/auth/google',
		{ token: tokenId },
		{
		  headers: {
			'Content-Type': 'application/json',
			'Accept': 'application/json'
		  },
		  withCredentials: true
		}
	  )
	  .then(response => {
		console.log('Success:', response.data);
		// Store JWT token
		localStorage.setItem('accessToken', response.data.accessToken);
		if (response.data.language) setLang(response.data.language);
        // Redirect user after successful login
        navigate('/dashboard');
	  })
	  .catch(error => console.error('Error:', error.response ? error.response.data : error.message));
	} catch (error) {
	  console.error('Login error', error);
	}
  };
  

const errorMessage = (error) => {
	console.log('Login failed:', error);
};

  // Handle password reset submission
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");

    if (newPasswordRef.current.value !== confirmPasswordRef.current.value) {
      setFunctionError(L.login.passwordsDoNotMatch);
      return;
    }

    try {
      const response = await axios.post("https://api.viralgpt.com/auth/reset-password", {
        token: resetToken,
        newPassword: newPasswordRef.current.value
      });

      setShowResetForm(false);
      setFunctionSuccess(L.login.resetSuccess);

    } catch (err) {
      setFunctionError(err.response?.data?.error || L.login.genericError);
    }
  };

  // Reset password form JSX
  const resetPasswordForm = (
    <Form onSubmit={handlePasswordReset}>
      <Form.Group className="mb-3">
        <Form.Label>{L.login.newPasswordLabel}</Form.Label>
        <Form.Control
          type="password"
          ref={newPasswordRef}
          required
          placeholder={L.login.newPasswordPlaceholder}
        />
        <small className="text-muted">{L.login.passwordHint}</small>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{L.login.confirmPasswordLabel}</Form.Label>
        <Form.Control
          type="password"
          ref={confirmPasswordRef}
          required
          placeholder={L.login.confirmPasswordPlaceholder}
        />
      </Form.Group>

      <Button type="submit" className="w-100">
        {L.login.resetPasswordButton}
      </Button>
    </Form>
  );



  // Validate token
  const checkAuthentication = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return false; // No token, not authenticated

    try {
      const response = await fetch("https://api.viralgpt.com/auth/validate", {
        method: "POST",
		credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({ token: accessToken }),
      });

      if (response.ok) {
        // If validation successful, ensure token is still in localStorage
        if (!localStorage.getItem("accessToken")) {
          localStorage.setItem("accessToken", accessToken);
        }
        return true;
      } else {
        // If token is invalid, remove it
        localStorage.removeItem("accessToken");
        return false;
      }
    } catch (err) {
      console.error("Authentication check failed:", err);
      return false;
    }
  };

  // Handle login form submission
  const login = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    try {
      const response = await axios.post("https://api.viralgpt.com/auth/login", {
        email: emailRef.current.value,
        password: passwordRef.current.value
      }, {
        withCredentials: true
      });

      // With axios, response.data contains the parsed response body
      const { accessToken } = response.data;

      // Save token and navigate to dashboard
      localStorage.setItem("accessToken", accessToken);
      if (response.data.language) setLang(response.data.language);
      navigate("/dashboard");

    } catch (err) {
      console.error("Login error:", err);
      // Handle axios error response
      const errorMessage = err.response?.data?.error || L.login.genericError;
      setError(errorMessage);
    }
  };

  // Redirect to dashboard if already logged in
  useEffect(() => {
    const redirectIfAuthenticated = async () => {
      const isAuthenticated = await checkAuthentication();
      if (isAuthenticated) {
        navigate("/dashboard"); // Redirect if already logged in
      } else {
        setLoading(false); // Stop loading if not logged in
      }
    };

    redirectIfAuthenticated();
  }, [navigate]);

  const [showModal, setShowModal] = useState(false);
  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const resetPassword = () => {
    setShowModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
		const response = await fetch("https://api.viralgpt.com/auth/request-reset", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				email: resetEmailRef.current.value,
			}),
		});
		if (response.ok) {
			setFunctionSuccess(L.login.resetSent);
		} else {
			const errorData = await response.json();
			setFunctionError(errorData.error || L.login.resetFailed);
		}	
	} catch (err) {
		console.error("Reset password error:", err);
		setFunctionError(L.login.genericError);
	}
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "20%" }}>{L.login.loading}</div>;
  }

  return (
    <div>
	<h3 className="m-2">
		<a href="/" className="text-decoration-none">
			<span className="text-2xl font-semibold text-gray-900 d-flex align-items-center">
				<img 
					src="/images/logo-icon.png" 
					alt="Logo" 
					style={{width: '30px', height: '30px', marginRight: '8px'}} 
				/>
				<span className="text-dark">ViralGPT</span>
			</span>
		</a>
	</h3>

    <Alert 
      variant="danger" 
      message={functionError} 
      show={!!functionError} 
      onClose={() => setFunctionError("")} 
    />
        
    <Alert 
      variant="success" 
      message={functionSuccess} 
      show={!!functionSuccess} 
      onClose={() => setFunctionSuccess("")} 
    />

		{showResetForm && (
			<Modal show={showResetForm} onHide={() => setShowResetForm(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>{L.login.resetModalTitle}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{resetPasswordForm}
				</Modal.Body>
			</Modal>
		)}

	  <Modal show={showModal} onHide={handleCloseModal} centered>
		<Modal.Header closeButton>
			<Modal.Title>{L.login.resetModalTitle}</Modal.Title>
		</Modal.Header>
		<Modal.Body>
			<p className="text-muted mb-4">
				{L.login.resetModalBody}
			</p>
			<Form>
				<Form.Group className="mb-4" controlId="resetEmail">
					<Form.Label>{L.login.emailAddressLabel}</Form.Label>
					<Form.Control 
						type="email" 
						placeholder={L.login.emailPlaceholder}
						className="form-control-lg" 
						ref={resetEmailRef}
					/>
				</Form.Group>
				<div className="d-grid">
					<Button 
						variant="primary" 
						type="submit"
						size="lg"
						className="bg-instagram"
						onClick={handleResetPassword}
					>
						{L.login.sendResetInstructions}
					</Button>
				</div>
			</Form>
		</Modal.Body>
	  </Modal>
      <Container className="justify-content-center text-center max-w-lg mt-12" fluid>

        <Alert 
          variant="danger" 
          message={"email_pass_login"} 
          show={!!error} 
          onClose={() => setError("")} 
        />   

        <Form onSubmit={login} className="bg-white shadow-lg p-4 mt-12">
          <h1 className="font-bold text-dark">{L.login.heading}</h1>
          <small className="text-muted">
            {L.login.subheading}
          </small>
          <Form.Group className="mb-3 mt-6" controlId="formBasicEmail">
            <Form.Control ref={emailRef} type="email" placeholder={L.login.enterEmailPlaceholder} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Control ref={passwordRef} type="password" placeholder={L.login.passwordPlaceholder} required />
          </Form.Group>

          <Button variant="primary" type="submit" className="bg-instagram w-full">
            {L.login.loginButton}
          </Button>
		  <br></br> <br></br>
		  <center>
		  <GoogleOAuthProvider clientId="255966676242-fhjo5pkp2eo7argo8oghm2u5u16253uk.apps.googleusercontent.com">
            <GoogleLogin
                onSuccess={successMessage}
                onError={errorMessage}
            />
        </GoogleOAuthProvider>
		</center>
		  <small className="d-block text-muted mt-6 cursor-pointer">
		  <span onClick={resetPassword} className="font-bold">{L.login.forgotPassword}</span>
		  </small>
        </Form>
		<small className="d-block text-muted mt-6 font-bold">
          {L.login.noAccount}
        </small>


      </Container>
    </div>
  );
};

export default Home;
